import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const OldSchoolTetris = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro" | "tutorial" | "play" | "results"
  const [difficulty, setDifficulty] = useState(1); // 1 (Easy), 2 (Medium), 3 (Hard)

  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef(null);

  // ----------------------------------
  //         TETRIS BOARD LOGIC
  // ----------------------------------
  const rows = 20;
  const cols = 10;
  const [board, setBoard] = useState(
    Array(rows)
      .fill()
      .map(() => Array(cols).fill(0))
  );

  // Example color map
  const colorMap = {
    0: "bg-slate-700/50", // Empty cells
    1: "bg-cyan-500", // I piece
    2: "bg-yellow-400", // O piece
    3: "bg-purple-500", // T piece
    4: "bg-green-500", // S piece
    5: "bg-red-500", // Z piece
    6: "bg-blue-500", // J piece
    7: "bg-orange-500", // L piece
  };

  // Tetromino definitions (each number represents a unique tetromino)
  const tetrominoes = {
    I: [
      [
        [0, 0, 0, 0],
        [1, 1, 1, 1],
        [0, 0, 0, 0],
        [0, 0, 0, 0],
      ],
      [
        [0, 0, 1, 0],
        [0, 0, 1, 0],
        [0, 0, 1, 0],
        [0, 0, 1, 0],
      ],
    ],
    O: [
      [
        [2, 2],
        [2, 2],
      ],
    ],
    T: [
      [
        [0, 3, 0],
        [3, 3, 3],
        [0, 0, 0],
      ],
      [
        [0, 3, 0],
        [0, 3, 3],
        [0, 3, 0],
      ],
      [
        [0, 0, 0],
        [3, 3, 3],
        [0, 3, 0],
      ],
      [
        [0, 3, 0],
        [3, 3, 0],
        [0, 3, 0],
      ],
    ],
    S: [
      [
        [0, 4, 4],
        [4, 4, 0],
        [0, 0, 0],
      ],
      [
        [0, 4, 0],
        [0, 4, 4],
        [0, 0, 4],
      ],
    ],
    Z: [
      [
        [5, 5, 0],
        [0, 5, 5],
        [0, 0, 0],
      ],
      [
        [0, 0, 5],
        [0, 5, 5],
        [0, 5, 0],
      ],
    ],
    J: [
      [
        [6, 0, 0],
        [6, 6, 6],
        [0, 0, 0],
      ],
      [
        [0, 6, 6],
        [0, 6, 0],
        [0, 6, 0],
      ],
      [
        [0, 0, 0],
        [6, 6, 6],
        [0, 0, 6],
      ],
      [
        [0, 6, 0],
        [0, 6, 0],
        [6, 6, 0],
      ],
    ],
    L: [
      [
        [0, 0, 7],
        [7, 7, 7],
        [0, 0, 0],
      ],
      [
        [0, 7, 0],
        [0, 7, 0],
        [0, 7, 7],
      ],
      [
        [0, 0, 0],
        [7, 7, 7],
        [7, 0, 0],
      ],
      [
        [7, 7, 0],
        [0, 7, 0],
        [0, 7, 0],
      ],
    ],
  };
  const tetrominoKeys = Object.keys(tetrominoes);

  // Current and next tetromino pieces
  const [currentPiece, setCurrentPiece] = useState(null); // { type, rotationIndex, position: { row, col } }
  const [nextPiece, setNextPiece] = useState(null);

  // Scoring & Performance
  const [score, setScore] = useState(0);
  const [linesCleared, setLinesCleared] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // UI / Tutorial
  const [tutorialStep, setTutorialStep] = useState(0);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);

  // ----------------------------------
  //        FETCH HIGH SCORES
  // ----------------------------------
  useEffect(() => {
    if (!organizationId) return;
    const fetchScores = async () => {
      try {
        const docRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "old-school-tetris"
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scoresForDifficulty =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scoresForDifficulty);
        } else {
          setHighScores([]);
        }
      } catch (err) {
        console.error("Error fetching high scores:", err);
      }
    };
    fetchScores();
  }, [organizationId, difficulty]);

  // ----------------------------------
  //         TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // ----------------------------------
  //         TETRIS GAME LOGIC
  // ----------------------------------

  // Initialize an empty board
  const initializeBoard = () =>
    Array(rows)
      .fill()
      .map(() => Array(cols).fill(0));

  // Return a random tetromino with its initial rotation and centered position
  const getRandomPiece = () => {
    const type =
      tetrominoKeys[Math.floor(Math.random() * tetrominoKeys.length)];
    const rotations = tetrominoes[type];
    const rotationIndex = 0;
    const pieceMatrix = rotations[rotationIndex];
    const pieceWidth = pieceMatrix[0].length;
    const startCol = Math.floor((cols - pieceWidth) / 2);
    return { type, rotationIndex, position: { row: 0, col: startCol } };
  };

  // Check if the piece collides with board boundaries or fixed blocks
  const checkCollision = (
    piece,
    board,
    deltaRow = 0,
    deltaCol = 0,
    newRotationIndex = null
  ) => {
    const rotations = tetrominoes[piece.type];
    const rotation =
      rotations[
        newRotationIndex !== null ? newRotationIndex : piece.rotationIndex
      ];
    for (let r = 0; r < rotation.length; r++) {
      for (let c = 0; c < rotation[r].length; c++) {
        if (rotation[r][c]) {
          const newRow = piece.position.row + r + deltaRow;
          const newCol = piece.position.col + c + deltaCol;
          if (newRow < 0 || newRow >= rows || newCol < 0 || newCol >= cols)
            return true;
          if (board[newRow][newCol] !== 0) return true;
        }
      }
    }
    return false;
  };

  // Merge the falling piece into the board grid
  const mergePieceToBoard = (piece, board) => {
    const newBoard = board.map((row) => row.slice());
    const rotations = tetrominoes[piece.type];
    const rotation = rotations[piece.rotationIndex];
    for (let r = 0; r < rotation.length; r++) {
      for (let c = 0; c < rotation[r].length; c++) {
        if (rotation[r][c]) {
          const newRow = piece.position.row + r;
          const newCol = piece.position.col + c;
          if (newRow >= 0) {
            newBoard[newRow][newCol] = rotation[r][c];
          }
        }
      }
    }
    return newBoard;
  };

  // Remove complete lines from the board and return new board along with cleared line count
  const clearLines = (board) => {
    let lines = 0;
    const newBoard = board.filter((row) => {
      if (row.every((cell) => cell !== 0)) {
        lines++;
        return false;
      }
      return true;
    });
    while (newBoard.length < rows) {
      newBoard.unshift(Array(cols).fill(0));
    }
    return { newBoard, lines };
  };

  // Drop the piece one row; if collision occurs, merge and spawn new piece
  const dropPiece = () => {
    if (!currentPiece) return;
    if (!checkCollision(currentPiece, board, 1, 0)) {
      setCurrentPiece({
        ...currentPiece,
        position: {
          row: currentPiece.position.row + 1,
          col: currentPiece.position.col,
        },
      });
    } else {
      let newBoard = mergePieceToBoard(currentPiece, board);
      const { newBoard: clearedBoard, lines } = clearLines(newBoard);
      if (lines > 0) {
        setLinesCleared((prev) => prev + lines);
        // Score increases based on number of lines cleared and difficulty level
        setScore((prev) => prev + 100 * lines * difficulty);
      }
      setBoard(clearedBoard);
      const next = nextPiece ? nextPiece : getRandomPiece();
      if (checkCollision(next, clearedBoard, 0, 0)) {
        endGame();
        return;
      }
      setCurrentPiece(next);
      setNextPiece(getRandomPiece());
    }
  };

  // Set an interval to drop the piece based on difficulty
  const dropIntervalRef = useRef(null);
  useEffect(() => {
    if (isPlaying && !isPaused) {
      const intervalTime =
        difficulty === 1 ? 800 : difficulty === 2 ? 600 : 400;
      dropIntervalRef.current = setInterval(() => {
        dropPiece();
      }, intervalTime);
      return () => clearInterval(dropIntervalRef.current);
    }
  }, [isPlaying, isPaused, currentPiece, board]);

  // ----------------------------------
  //         MOVEMENT CONTROLS
  // ----------------------------------
  const moveLeft = () => {
    if (!currentPiece || !isPlaying || isPaused) return;
    if (!checkCollision(currentPiece, board, 0, -1)) {
      setCurrentPiece({
        ...currentPiece,
        position: {
          ...currentPiece.position,
          col: currentPiece.position.col - 1,
        },
      });
    }
  };

  const moveRight = () => {
    if (!currentPiece || !isPlaying || isPaused) return;
    if (!checkCollision(currentPiece, board, 0, 1)) {
      setCurrentPiece({
        ...currentPiece,
        position: {
          ...currentPiece.position,
          col: currentPiece.position.col + 1,
        },
      });
    }
  };

  const rotatePiece = () => {
    if (!currentPiece || !isPlaying || isPaused) return;
    const newRotationIndex =
      (currentPiece.rotationIndex + 1) % tetrominoes[currentPiece.type].length;
    if (!checkCollision(currentPiece, board, 0, 0, newRotationIndex)) {
      setCurrentPiece({ ...currentPiece, rotationIndex: newRotationIndex });
    }
  };

  const dropDown = () => {
    if (!currentPiece || !isPlaying || isPaused) return;

    // Calculate the final row where the current piece can drop
    let finalRow = currentPiece.position.row;
    // Create a temporary piece object to test collisions
    while (
      !checkCollision(
        {
          ...currentPiece,
          position: { ...currentPiece.position, row: finalRow + 1 },
        },
        board,
        0,
        0
      )
    ) {
      finalRow++;
    }

    // Update current piece once with the final landing row
    setCurrentPiece((prev) => ({
      ...prev,
      position: { ...prev.position, row: finalRow },
    }));

    // Merge the piece into the board and spawn the next piece
    dropPiece();
  };

  // ----------------------------------
  //         START & END GAME
  // ----------------------------------
  const startGame = () => {
    setScore(0);
    setLinesCleared(0);
    setTimeElapsed(0);
    setBoard(initializeBoard());
    const piece = getRandomPiece();
    setCurrentPiece(piece);
    setNextPiece(getRandomPiece());
    setIsPlaying(true);
    setIsPaused(false);
    setMessage("");
    setShowMessage(false);
  };

  const endGame = () => {
    setIsPlaying(false);
    setGameMode("results");
    // Scoring tie-breakers:
    // 1. Higher score wins.
    // 2. If scores are equal, lower time elapsed wins.
    // 3. If still equal, higher linesCleared wins.
  };

  // ----------------------------------
  //         SAVE SCORE
  // ----------------------------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;
    try {
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }
      const gameData = {
        gameType: "old-school-tetris",
        gameId: "old-school-tetris",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        timeElapsed,
        linesCleared,
        timestamp: new Date(),
      };

      // Composite scoring comparator
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.timeElapsed !== b.timeElapsed)
          return a.timeElapsed - b.timeElapsed;
        return b.linesCleared - a.linesCleared;
      };

      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "old-school-tetris"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const diffKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[diffKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[diffKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setGameStarted(false);
    } catch (err) {
      console.error("Error saving Tetris score:", err);
    }
  };

  // ----------------------------------
  //         UTILITIES
  // ----------------------------------
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-yellow-300">
              Old School Tetris
            </h1>
            <div className="p-2 bg-yellow-500/10 rounded-full">
              <Brain className="text-yellow-400" size={20} />
            </div>
          </div>
          <button
            onClick={() => {}}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Outer Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Old School Tetris
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Experience the classic Tetris game. Rotate and move falling
                  tetrominoes to complete lines and earn points.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Classic Gameplay
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Relive the original Tetris experience with intuitive
                    controls.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Fast-Paced Action
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Challenge yourself with increasing speed and difficulty.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Compete with others using clear tiebreakers.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl === 1 ? "Easy" : lvl === 2 ? "Medium" : "Hard"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (s.userName && s.userName.includes(" ")) {
                            const parts = s.userName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={`${s.userId}-${index}`}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {formatTime(s.timeElapsed)}
                              </td>
                              <td className="py-2 px-4 text-center text-yellow-300 font-medium">
                                {s.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Old School Tetris
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        Tetris is a falling block puzzle game. Your goal is to
                        rotate and align tetrominoes to fill horizontal lines
                        which then clear.
                      </p>
                      <p>
                        Use the control buttons to move left, right, rotate, or
                        drop the piece faster.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        Use the Left and Right buttons to reposition the falling
                        piece.
                      </p>
                      <p>Tap Rotate to change the piece’s orientation.</p>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        Press Down to drop the piece quickly. When a piece
                        lands, any complete line is cleared.
                      </p>
                      <p>
                        Clearing multiple lines at once will boost your score!
                      </p>
                    </>
                  )}
                  {tutorialStep === 3 && (
                    <>
                      <p>
                        The game ends when new pieces cannot be spawned at the
                        top.
                      </p>
                      <p>Good luck and enjoy the challenge!</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}
          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-4 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => {
                      setGameStarted(true);
                      startGame();
                    }}
                    className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                  >
                    Start Game
                  </button>
                </div>
              )}

              {/* Message Toast */}
              <AnimatePresence>
                {showMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-slate-800 px-4 py-2 rounded-lg shadow-lg z-40"
                  >
                    {message}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mb-4">
                <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 px-3 py-1 rounded-lg">
                  <span className="text-yellow-200 font-medium text-sm md:text-base">
                    Difficulty:{" "}
                    {difficulty === 1
                      ? "Easy"
                      : difficulty === 2
                      ? "Medium"
                      : "Hard"}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-3 py-1 rounded-lg flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-white font-mono text-sm">
                      {formatTime(timeElapsed)}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsPaused(!isPaused)}
                    className="ml-2 p-1 md:p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 hover:text-white transition-colors text-sm"
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                </div>
              </div>

              {isPaused && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/80">
                  <div className="text-center p-6 bg-slate-800 rounded-xl">
                    <h3 className="text-xl font-bold text-white mb-4">
                      Game Paused
                    </h3>
                    <button
                      onClick={() => setIsPaused(false)}
                      className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                    >
                      Resume Game
                    </button>
                  </div>
                </div>
              )}

              {/* Responsive layout that works on both mobile and desktop */}
              <div
                className="flex flex-col items-center mx-auto"
                style={{ maxWidth: "350px" }}
              >
                {/* Game Info - moved to top on mobile */}
                <div className="w-full mb-4 flex justify-between bg-slate-800/50 border border-slate-700 rounded-lg p-2">
                  <div className="flex items-center">
                    <span className="text-slate-400 text-sm mr-2">Score:</span>
                    <span className="text-yellow-300 font-medium">{score}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-slate-400 text-sm mr-2">Lines:</span>
                    <span className="text-green-300 font-medium">
                      {linesCleared}
                    </span>
                  </div>
                </div>

                {/* Tetris Board - with constrained size */}
                <div className="relative bg-slate-800/70 border border-slate-700 rounded-xl p-1 mb-4">
                  <div className="grid grid-rows-20 grid-cols-10 gap-0.5">
                    {board.map((row, rowIdx) =>
                      row.map((cell, colIdx) => {
                        let cellValue = cell;

                        if (currentPiece) {
                          const rotations = tetrominoes[currentPiece.type];
                          const shape = rotations[currentPiece.rotationIndex];
                          for (let r = 0; r < shape.length; r++) {
                            for (let c = 0; c < shape[r].length; c++) {
                              if (shape[r][c]) {
                                const pieceRow = currentPiece.position.row + r;
                                const pieceCol = currentPiece.position.col + c;
                                if (
                                  pieceRow === rowIdx &&
                                  pieceCol === colIdx
                                ) {
                                  cellValue = shape[r][c];
                                }
                              }
                            }
                          }
                        }

                        // Use our color map
                        const cellBg = colorMap[cellValue] || colorMap[0];

                        return (
                          <div
                            key={`${rowIdx}-${colIdx}`}
                            className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${cellBg} border border-slate-800`}
                          />
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Controls - always visible on screen now */}
                <div className="w-full">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      onClick={moveLeft}
                      disabled={isPaused}
                      className="py-2 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Left
                    </button>
                    <button
                      onClick={moveRight}
                      disabled={isPaused}
                      className="py-2 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Right
                    </button>
                    <button
                      onClick={rotatePiece}
                      disabled={isPaused}
                      className="py-2 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Rotate
                    </button>
                    <button
                      onClick={dropDown}
                      disabled={isPaused}
                      className="py-2 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      Down
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to end the game early?"
                        )
                      ) {
                        endGame();
                      }
                    }}
                    className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors text-sm"
                  >
                    End Game
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Over
                </h2>
                <p className="text-slate-300">Your Tetris session has ended.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-yellow-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-yellow-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty === 1
                          ? "Easy"
                          : difficulty === 2
                          ? "Medium"
                          : "Hard"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Time Spent</span>
                      <span className="text-white">
                        {formatTime(timeElapsed)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Lines Cleared</span>
                      <span className="text-green-300">{linesCleared}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Brain size={20} className="text-yellow-300" />
                    Cognitive Benefits
                  </h3>
                  <div className="space-y-3 text-slate-300">
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Spatial Awareness:
                      </span>{" "}
                      Tetris challenges your ability to quickly visualize and
                      manipulate shapes.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Problem-Solving:
                      </span>{" "}
                      Strategically positioning pieces boosts your critical
                      thinking.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Reaction Time:
                      </span>{" "}
                      Fast-paced gameplay improves your reflexes.
                    </p>
                    <p className="mt-4 text-sm">
                      Regular practice with Tetris can help enhance these
                      skills.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6 bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300">
                <h3 className="text-white font-medium mb-2">
                  How Scoring Works
                </h3>
                <p>
                  Score: Points earned from clearing lines.
                  <br />
                  Time Bonus: Faster completions add to your score.
                  <br />
                  Lines Cleared: More lines cleared equals a higher final score.
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                <button
                  onClick={saveScore}
                  className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Trophy size={18} />
                  Save Score
                </button>

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OldSchoolTetris;
